import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FormlyModule } from '@ngx-formly/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SvgIconComponent } from 'angular-svg-icon'
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton'
import { forkJoin, Observable, Subscription, tap } from 'rxjs'

import {
  ActiveSorting,
  TableBaseAbstract,
} from '../../abstracts/table-base.abstract'
import { BackButtonComponent } from '../../components/back-button/back-button.component'
import { ButtonComponent } from '../../components/button/button.component'
import { MasterAddressesComponent } from '../../components/master-addresses/master-addresses.component'
import { PageHeadlineComponent } from '../../components/page-headline/page-headline.component'
import { SearchBoxComponent } from '../../components/search-box/search-box.component'
import { TableDataBase } from '../../components/table/table.component'
import { Address, ValidatedAddress } from '../../models/address.model'
import { Snapshot } from '../../models/snapshot.model'
import { AddressRepository } from '../../repositories/address/address.repository'
import { Expression } from '../../repositories/base.repository'
import { SnapshotRepository } from '../../repositories/snapshot/snaphot.repository'

export type TableData = TableDataBase & Address

@Component({
  selector: 'app-snapshot-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    FormlyModule,
    PageHeadlineComponent,
    SvgIconComponent,
    TranslatePipe,
    SearchBoxComponent,
    NzSkeletonComponent,
    BackButtonComponent,
    MasterAddressesComponent,
  ],
  templateUrl: './snapshot-detail.component.html',
})
export class SnapshotDetailComponent
  extends TableBaseAbstract
  implements OnInit, OnDestroy
{
  @Input() public id!: string

  public override activeSorting: ActiveSorting = [
    {
      key: 'plz',
      value: 'descend',
    },
  ]

  public override tableData: TableData[] = []
  public snapshot?: Snapshot
  public snapshotFilters?: string[]

  public typeToken!: { item: string; currentRow: TableData }

  private subscription!: Subscription

  public constructor(
    private readonly translateService: TranslateService,
    private readonly addressRepository: AddressRepository,
    private readonly snapshotRepository: SnapshotRepository
  ) {
    super()
  }

  public ngOnInit(): void {
    this.subscription = forkJoin([
      this.fetchDetailData(),
      this.fetchTableData(),
    ]).subscribe()
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  public download(): void {
    this.snapshotRepository.downloadCSV(this.id)
  }

  public onSearchFilterChange(sword: string): void {
    this.sword = sword
    this.pageIndex = 1
    this.updateTableData()
  }

  protected updateTableData(): void {
    this.isLoading = true
    this.subscription = this.fetchTableData().subscribe()
  }

  private fetchDetailData(): Observable<unknown> {
    return forkJoin([
      this.snapshotRepository.getById(this.id),
      this.translateService.get('models.address.county.label'), // wait for translation file
    ]).pipe(
      tap(([response]) => {
        this.snapshot = {
          id: response.data.id,
          ...response.data.attributes,
        }
        const filters: string[] = []
        for (const expression of response.data.attributes.filter) {
          if (this.snapshotRepository.isSingleScalarExpression(expression)) {
            const translated = this.translateService.instant(
              `models.address.${expression.name}.label`
            )
            filters.push(`${translated}: ${expression.val}`)
          } else if (
            this.snapshotRepository.isLogicalOrExpression(expression)
          ) {
            const list: string[] = []
            let name: string = ''
            for (const element of expression.or) {
              if (this.snapshotRepository.isSingleScalarExpression(element)) {
                list.push(element.val as string)
                name = element.name
              }
            }
            if (name) {
              const translated = this.translateService.instant(
                `models.address.${name}.label`
              )
              filters.push(`${translated}: ` + list.join(', '))
            }
          }
        }
        if (filters.length) {
          this.snapshotFilters = filters
        }
      })
    )
  }

  private fetchTableData(): Observable<unknown> {
    return this.addressRepository
      .getSnapshotAddresses(this.id, {
        limit: this.pageSize,
        page: this.pageIndex,
        filter: this.getFilter(),
        sorting: this.getSorting(),
      })
      .pipe(
        tap((response) => {
          this.total = response.meta.totalCount
          this.tableData = response.data.map(
            (address) =>
              ({
                id: address.id,
                ...address.attributes,
              }) as TableData
          )
          this.isLoading = false
        })
      )
  }

  protected getFilter(): Expression<ValidatedAddress>[] {
    const filter: Expression<ValidatedAddress>[] = []

    if (this.sword) {
      filter.push(
        this.addressRepository.getSearchQuery(this.sword, [
          'county',
          'place',
          'plz',
          'streetName',
          'houseNumber',
          'houseNumberAddition',
          'marking',
        ])
      )
    }

    return filter
  }
}
